import { Box, Button, Card, CardContent, CircularProgress, Stack, TextField } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useToast } from "@src/appV2/lib";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useState } from "react";

import type {
  CognitoAuthentication,
  CognitoUser,
} from "../../../../lib/cognito/src/lib/amplifyAuth";

interface PhoneAuthTestProps {
  cognitoAuth: CognitoAuthentication;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setResult: (result: string) => void;
  setApiResult: (result: string) => void;
  currentUser: CognitoUser | undefined;
  setCurrentUser: (user: CognitoUser | undefined) => void;
}

export function PhoneAuthTest(props: PhoneAuthTestProps) {
  const {
    cognitoAuth,
    isLoading,
    setIsLoading,
    setResult,
    setApiResult,
    currentUser,
    setCurrentUser,
  } = props;
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const { showSuccessToast, showErrorToast } = useToast();

  const handleSendOtp = async () => {
    if (!phoneNumber) {
      showErrorToast("Please enter a phone number");
      return;
    }

    setIsLoading(true);
    setResult("");
    setApiResult("");

    try {
      const response = await cognitoAuth.requestPhoneOtp(phoneNumber);
      setOtpSent(true);
      showSuccessToast(`${response.message}\n\nCheck your phone for the code.`);
    } catch (error) {
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error.message as string)
          : "An unknown error occurred";
      showErrorToast(`Error sending OTP: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (!phoneNumber || !otpCode) {
      showErrorToast("Please enter both phone number and OTP code");
      return;
    }

    setIsLoading(true);

    try {
      const credentials = await cognitoAuth.confirmPhoneOtp(otpCode);
      const userData = JSON.stringify(credentials.user, null, 2);
      setCurrentUser(credentials.user);
      setResult(`Sign in successful!\n\nUser: ${userData}`);
      setOtpSent(false);
      setOtpCode("");
    } catch (error) {
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error.message as string)
          : "An unknown error occurred";
      showErrorToast(`Error verifying OTP: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelOtp = () => {
    setOtpSent(false);
    setOtpCode("");
  };

  return (
    <Card>
      <CardContent>
        <Text
          variant="h6"
          sx={(theme: Theme) => ({
            marginBottom: theme.spacing(3),
          })}
        >
          Phone OTP Authentication Test
        </Text>
        {!otpSent && (
          <Stack
            sx={(theme: Theme) => ({
              gap: theme.spacing(2),
            })}
          >
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              placeholder="Enter your phone number"
              type="tel"
              onChange={(event) => {
                setPhoneNumber(event.target.value);
              }}
            />
            <Button
              fullWidth
              variant="contained"
              disabled={isLoading || !phoneNumber || Boolean(currentUser)}
              onClick={handleSendOtp}
            >
              {isLoading ? <CircularProgress size={24} /> : "1. Send OTP"}
            </Button>
          </Stack>
        )}
        {otpSent && (
          <Stack
            sx={(theme: Theme) => ({
              gap: theme.spacing(2),
            })}
          >
            <TextField
              fullWidth
              label="OTP Code"
              value={otpCode}
              placeholder="Enter the OTP code"
              type="text"
              onChange={(event) => {
                setOtpCode(event.target.value);
              }}
            />
            <Box
              sx={(theme: Theme) => ({
                display: "flex",
                gap: theme.spacing(1),
              })}
            >
              <Button
                fullWidth
                variant="contained"
                color="success"
                disabled={isLoading || !phoneNumber || !otpCode || Boolean(currentUser)}
                onClick={handleConfirmOtp}
              >
                {isLoading ? <CircularProgress size={24} /> : "Confirm OTP"}
              </Button>
              <Button fullWidth variant="outlined" disabled={isLoading} onClick={handleCancelOtp}>
                Cancel
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
